import { BOMItem, ComponentVarianceResult, ProjectBudgetSummary, PhaseBudgetRollup } from '../types/bom';
import { RestorationPhase, RESTORATION_PHASES, PhotoLog, SubAssembly, HardwareBag } from '../types/teardown';
import { VehicleProfile, SubletExpense, LaborRecord, ProvenanceScoreBreakdown, ValuationSummary } from '../types/dossier';

export function roundToTwo(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Computes line-item cost and variance for a single BOM item.
 */
export function calculateComponentVariance(item: BOMItem): ComponentVarianceResult {
  const qty = Math.max(1, item.quantity || 1);
  const baseEstimated = (item.estimatedCost || 0) * qty;
  const baseActual = (item.actualCost || 0) * qty;
  const shipping = item.shippingCost || 0;
  const tax = item.taxCost || 0;
  const coreCharge = item.coreCharge || 0;
  const coreRefund = item.coreRefunded || 0;

  const netActual = baseActual + shipping + tax + coreCharge - coreRefund;
  const costVariance = netActual - baseEstimated;

  let variancePercentage = 0;
  if (baseEstimated > 0) {
    variancePercentage = (costVariance / baseEstimated) * 100;
  } else if (netActual > 0) {
    variancePercentage = 100;
  }

  return {
    baseEstimatedCost: roundToTwo(baseEstimated),
    netActualCost: roundToTwo(netActual),
    costVariance: roundToTwo(costVariance),
    variancePercentage: roundToTwo(variancePercentage),
    isFavorable: costVariance <= 0,
  };
}

/**
 * Calculates comprehensive project budget rollups and phase-by-phase matrices.
 */
export function calculateProjectBudgetSummary(
  allocatedBudget: number,
  items: BOMItem[]
): ProjectBudgetSummary {
  let totalEstimatedBOM = 0;
  let totalActualSpent = 0;
  let totalPendingCommitted = 0;
  let favorableCount = 0;
  let unfavorableCount = 0;

  const phaseBreakdowns = {} as Record<RestorationPhase, PhaseBudgetRollup>;
  const phaseKeys = Object.keys(RESTORATION_PHASES) as RestorationPhase[];
  for (const p of phaseKeys) {
    phaseBreakdowns[p] = {
      phase: p,
      phaseTitle: RESTORATION_PHASES[p].title,
      itemCount: 0,
      neededCount: 0,
      sourcedCount: 0,
      installedCount: 0,
      estimatedTotal: 0,
      actualSpent: 0,
      pendingCommitted: 0,
      projectedTotal: 0,
      variance: 0,
      utilizationPct: 0,
    };
  }

  for (const item of items) {
    const calc = calculateComponentVariance(item);
    const p = item.phase;
    const rollup = phaseBreakdowns[p] || (phaseBreakdowns[p] = {
      phase: p,
      phaseTitle: p,
      itemCount: 0,
      neededCount: 0,
      sourcedCount: 0,
      installedCount: 0,
      estimatedTotal: 0,
      actualSpent: 0,
      pendingCommitted: 0,
      projectedTotal: 0,
      variance: 0,
      utilizationPct: 0,
    });

    rollup.itemCount += 1;
    totalEstimatedBOM += calc.baseEstimatedCost;
    rollup.estimatedTotal += calc.baseEstimatedCost;

    if (item.status === 'Needed') {
      rollup.neededCount += 1;
      totalPendingCommitted += calc.baseEstimatedCost;
      rollup.pendingCommitted += calc.baseEstimatedCost;
    } else {
      // Sourced or Installed
      if (item.status === 'Sourced') rollup.sourcedCount += 1;
      if (item.status === 'Installed') rollup.installedCount += 1;

      totalActualSpent += calc.netActualCost;
      rollup.actualSpent += calc.netActualCost;

      if (calc.isFavorable) favorableCount += 1;
      else unfavorableCount += 1;
    }
  }

  // Calculate phase derived metrics
  for (const p of phaseKeys) {
    const r = phaseBreakdowns[p];
    r.projectedTotal = roundToTwo(r.actualSpent + r.pendingCommitted);
    r.variance = roundToTwo(r.projectedTotal - r.estimatedTotal);
    r.utilizationPct = r.estimatedTotal > 0
      ? roundToTwo((r.actualSpent / r.estimatedTotal) * 100)
      : 0;
    r.estimatedTotal = roundToTwo(r.estimatedTotal);
    r.actualSpent = roundToTwo(r.actualSpent);
    r.pendingCommitted = roundToTwo(r.pendingCommitted);
  }

  const projectedTotalExpenditure = roundToTwo(totalActualSpent + totalPendingCommitted);
  const remainingBudget = roundToTwo(allocatedBudget - totalActualSpent);
  const projectedBudgetVariance = roundToTwo(projectedTotalExpenditure - allocatedBudget);
  const budgetUtilizationPct = allocatedBudget > 0
    ? roundToTwo((totalActualSpent / allocatedBudget) * 100)
    : 0;

  return {
    totalAllocatedBudget: roundToTwo(allocatedBudget),
    totalEstimatedBOM: roundToTwo(totalEstimatedBOM),
    totalActualSpent: roundToTwo(totalActualSpent),
    totalPendingCommitted: roundToTwo(totalPendingCommitted),
    projectedTotalExpenditure,
    remainingBudget,
    projectedBudgetVariance,
    budgetUtilizationPct,
    favorableItemCount: favorableCount,
    unfavorableItemCount: unfavorableCount,
    phaseBreakdowns,
  };
}

/**
 * Calculates Provenance Completeness Score (0-100) for Hagerty/Grundy underwriting.
 */
export function calculateProvenanceScore(params: {
  vehicle: VehicleProfile;
  photos: PhotoLog[];
  bomItems: BOMItem[];
  subAssemblies: SubAssembly[];
  hardwareBags: HardwareBag[];
  subletExpenses: SubletExpense[];
}): ProvenanceScoreBreakdown {
  const { vehicle, photos, bomItems, subAssemblies, hardwareBags, subletExpenses } = params;

  // 1. VIN & Title Verification (15 pts)
  let vinEarned = 0;
  if (vehicle.vin && vehicle.vin.trim().length >= 5) vinEarned += 8;
  if (vehicle.cowlTag && vehicle.cowlTag.trim().length > 0) vinEarned += 4;
  if (photos.some(p => p.tags.includes('vin_plate') || p.tags.includes('cowl_tag') || p.tags.includes('fender_tag'))) vinEarned += 3;

  // 2. 7-Phase Coverage (25 pts)
  const coveredPhases = new Set(photos.map(p => p.phase));
  const phaseRatio = coveredPhases.size / 7;
  const phaseEarned = Math.round(phaseRatio * 25);

  // 3. Photo Evidence & Before/After Pairs (15 pts)
  let photoEarned = 0;
  const dossierPhotos = photos.filter(p => p.includeInDossier);
  if (dossierPhotos.length >= 10) photoEarned += 7;
  else if (dossierPhotos.length >= 5) photoEarned += 4;
  const beforeAfterPairs = photos.filter(p => p.category === 'before_after_pair' || Boolean(p.afterImageUrl));
  if (beforeAfterPairs.length >= 4) photoEarned += 8;
  else if (beforeAfterPairs.length >= 1) photoEarned += 4;

  // 4. Receipt Audit Trail (20 pts)
  let receiptEarned = 0;
  if (bomItems.length > 0) {
    const purchasedItems = bomItems.filter(i => i.status !== 'Needed');
    const itemsWithReceipt = purchasedItems.filter(i => i.receiptNumber || i.receiptUrl);
    const receiptRatio = itemsWithReceipt.length / Math.max(1, purchasedItems.length);
    receiptEarned = Math.round(receiptRatio * 20);
  }

  // 5. Hardware & Sub-Assembly Cataloging (15 pts)
  let hardwareEarned = 0;
  if (subAssemblies.length >= 5) hardwareEarned += 7;
  else if (subAssemblies.length > 0) hardwareEarned += 3;
  if (hardwareBags.length >= 10) hardwareEarned += 8;
  else if (hardwareBags.length > 0) hardwareEarned += 4;

  // 6. Sublet Documentation (10 pts)
  let subletEarned = 0;
  if (subletExpenses.length > 0) {
    const documented = subletExpenses.filter(s => s.invoiceNumber && s.invoiceNumber.trim().length > 0);
    subletEarned = Math.round((documented.length / subletExpenses.length) * 10);
  } else {
    subletEarned = 8;
  }

  const total = Math.min(100, vinEarned + phaseEarned + photoEarned + receiptEarned + hardwareEarned + subletEarned);

  let ratingBand: ProvenanceScoreBreakdown['ratingBand'] = 'Preliminary Draft';
  if (total >= 90) ratingBand = 'Elite Documentation';
  else if (total >= 75) ratingBand = 'Strong Provenance';
  else if (total >= 50) ratingBand = 'Moderate Proof';

  return {
    vinVerified: { earned: vinEarned, max: 15 },
    phaseCoverage: { earned: phaseEarned, max: 25 },
    photoEvidence: { earned: photoEarned, max: 15 },
    receiptAuditTrail: { earned: receiptEarned, max: 20 },
    hardwareCataloging: { earned: hardwareEarned, max: 15 },
    subletDocumentation: { earned: subletEarned, max: 10 },
    totalScore: total,
    ratingBand,
  };
}

/**
 * Calculates Agreed-Value Valuation summary.
 */
export function calculateValuationSummary(params: {
  vehicle: VehicleProfile;
  bomItems: BOMItem[];
  subletExpenses: SubletExpense[];
  laborRecords: LaborRecord[];
  provenanceScore: ProvenanceScoreBreakdown;
  appreciationBufferPct?: number;
}): ValuationSummary {
  const { vehicle, bomItems, subletExpenses, laborRecords, provenanceScore } = params;
  const bufferPct = params.appreciationBufferPct ?? 10;

  const acquisitionCost = vehicle.acquisitionCost || 0;

  const totalBOMPartsCost = bomItems
    .filter(i => i.status === 'Sourced' || i.status === 'Installed')
    .reduce((sum, item) => sum + calculateComponentVariance(item).netActualCost, 0);

  const totalSubletLaborCost = subletExpenses.reduce((sum, sub) => sum + (sub.cost || 0), 0);

  const totalInHouseLaborValuation = laborRecords.reduce(
    (sum, lab) => sum + (lab.hours || 0) * (lab.hourlyRate || 85),
    0
  );

  const totalInvestedCapital = roundToTwo(
    acquisitionCost + totalBOMPartsCost + totalSubletLaborCost + totalInHouseLaborValuation
  );

  const suggestedAgreedValue = roundToTwo(totalInvestedCapital * (1 + bufferPct / 100));

  return {
    acquisitionCost: roundToTwo(acquisitionCost),
    totalBOMPartsCost: roundToTwo(totalBOMPartsCost),
    totalSubletLaborCost: roundToTwo(totalSubletLaborCost),
    totalInHouseLaborValuation: roundToTwo(totalInHouseLaborValuation),
    totalInvestedCapital,
    appreciationBufferPct: bufferPct,
    suggestedAgreedValue,
    provenanceScore,
  };
}
